import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { captureSignupAttribution } from '@/utils/signupAttribution'
import { Routes, Route } from 'react-router-dom'
import { Toaster } from 'sonner'
import { Suspense, lazy } from 'react'
import { Navigate } from 'react-router-dom'
import { useThemeInit } from './hooks/useTheme'
import { usePromoMode } from './hooks/usePromoMode'
import { ProtectedRoute } from './components/auth/ProtectedRoute'
import { AppLayout } from './components/layout/AppLayout'
import { PageLoader } from './components/ui/page-loader'

// Lazy load pages for code splitting
const LandingPage = lazy(() => import('./pages/LandingPage'))
const LoginPage = lazy(() => import('./pages/LoginPage'))
const RegisterPage = lazy(() => import('./pages/RegisterPage'))
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'))
const PromotionPage = lazy(() => import('./pages/PromotionPage'))
const PricingPage = lazy(() => import('./pages/PricingPage'))
const AboutPage = lazy(() => import('./pages/AboutPage'))
const ContactPage = lazy(() => import('./pages/ContactPage'))
const AuthCallback = lazy(() => import('./pages/AuthCallback'))
const BoardsPageNew = lazy(() => import('./pages/BoardsPageNew'))
const BoardDetailPage = lazy(() => import('./pages/BoardDetailPage'))
const AdvancedAnalyticsPage = lazy(() => import('./pages/AdvancedAnalyticsPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const YouTubeExtendedAnalyticsPage = lazy(() => import('./pages/YouTubeExtendedAnalyticsPage'))
const InstagramAnalyticsPage = lazy(() => import('./pages/InstagramAnalyticsPage'))
const TikTokAnalyticsPage = lazy(() => import('./pages/TikTokAnalyticsPage'))
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicy'))
const TermOfServicePage = lazy(() => import('./pages/TermOfService'))
const VerificationAccountPage = lazy(() => import('./pages/VerificationAccountPage'))
const FAQPage = lazy(() => import('./pages/FAQPage'))
const PaymentPage = lazy(() => import('./pages/PaymentPage'))
const InvitationAcceptPage = lazy(() => import('./pages/InvitationAcceptPage'))
const BlogListPage = lazy(() => import('./pages/BlogListPage'))
const BlogDetailPage = lazy(() => import('./pages/BlogDetailPage'))
const ToolsIndexPage = lazy(() => import('./pages/tools/ToolsIndexPage'))
const PostPreviewPage = lazy(() => import('./pages/tools/PostPreviewPage'))
const ContentCalendarGeneratorPage = lazy(() => import('./pages/tools/ContentCalendarGeneratorPage'))
const EngagementCalculatorPage = lazy(() => import('./pages/tools/EngagementCalculatorPage'))
const ThumbnailDownloaderPage = lazy(() => import('./pages/tools/ThumbnailDownloaderPage'))
const TikTokMoneyCalculatorPage = lazy(() => import('./pages/tools/TikTokMoneyCalculatorPage'))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'))
const LoaderTestPage = lazy(() => import('./pages/LoaderTestPage'))


function App() {
  useThemeInit()
  usePromoMode()
  const location = useLocation()
  useEffect(() => { captureSignupAttribution(location, document.referrer) }, [location.search])

  return (
    <>
      <Toaster position="bottom-right" richColors closeButton />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/offers/:promotionId" element={<PromotionPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/invitation/:token" element={<InvitationAcceptPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermOfServicePage />} />
          <Route path="/verify-email" element={<VerificationAccountPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/payment" element={<PaymentPage />} />
          <Route path="/blogs" element={<BlogListPage />} />
          <Route path="/blogs/:slug" element={<BlogDetailPage />} />
          <Route path="/tools" element={<ToolsIndexPage />} />
          <Route path="/tools/post-preview" element={<PostPreviewPage />} />
          <Route path="/tools/content-calendar-generator" element={<ContentCalendarGeneratorPage />} />
          <Route path="/tools/engagement-calculator" element={<EngagementCalculatorPage />} />
          <Route path="/tools/thumbnail-downloader" element={<ThumbnailDownloaderPage />} />
          <Route path="/tools/tiktok-money-calculator" element={<TikTokMoneyCalculatorPage />} />
          <Route path="/loader-test" element={<LoaderTestPage />} />
          {/* Protected routes */}
          <Route element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }>
            <Route path="/dashboard" element={<Navigate to="/boards" replace />} />
            <Route path="/boards" element={<BoardsPageNew />} />
            <Route path="/boards/:boardId" element={<BoardDetailPage />} />
            <Route path="/boards/:boardId/youtube-analytics" element={<YouTubeExtendedAnalyticsPage />} />
            <Route path="/boards/:boardId/instagram-analytics" element={<InstagramAnalyticsPage />} />
            <Route path="/boards/:boardId/tiktok-analytics" element={<TikTokAnalyticsPage />} />
            <Route path="/analytics" element={<AdvancedAnalyticsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Route>

          {/* Catch all - 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </>
  )
}

export default App
